import isObject from 'lodash/isObject'
import type { AddressJson } from '@klear/address'
import { getAppEnv } from '@klear/runtime-env'
import { isProductionAppEnv } from '@klear/constants/app-env'
import {
  EXTERNAL_BANK_ACCOUNT_HOLDER_TYPE_INDIVIDUAL,
  EXTERNAL_BANK_ACCOUNT_TYPE_CHECKING,
  PAYMENT_NETWORK_ACH
} from '@klear/bank-account/constants'
import type {
  BankPaymentNetwork,
  ExternalBankAccountHolderType,
  ExternalBankAccountType
} from '@klear/bank-account/constants'
import type { AccountDetails, BillingDetails } from '@klear/bank-account/schema'
import { COUNTRY_CODE_US } from '@klear/constants/countries'

export type ExternalBankAccountAddressForm = {
  line1: string
  line2: string
  city: string
  state: string
  postal_code: string
  country: string
}

export type ExternalBankAccountForm = {
  nickname: string
  accountHolderType: ExternalBankAccountHolderType
  accountType: ExternalBankAccountType
  accountNumber: string
  routingNumber: string
  recipientName: string
  recipientEmail: string
  recipientPhone: string
  address: ExternalBankAccountAddressForm
  supportedPaymentNetworks: BankPaymentNetwork[]
}

export type ExternalBankAccountFormDirty = Partial<
  Record<Exclude<keyof ExternalBankAccountForm, 'address'>, boolean>
> & {
  address?: boolean | Partial<Record<keyof ExternalBankAccountAddressForm, boolean>>
}

export const EMPTY_EXTERNAL_BANK_ACCOUNT_FORM: ExternalBankAccountForm = {
  nickname: '',
  accountHolderType: EXTERNAL_BANK_ACCOUNT_HOLDER_TYPE_INDIVIDUAL,
  accountType: EXTERNAL_BANK_ACCOUNT_TYPE_CHECKING,
  accountNumber: '',
  routingNumber: '',
  recipientName: '',
  recipientEmail: '',
  recipientPhone: '',
  address: { line1: '', line2: '', city: '', state: '', postal_code: '', country: '' },
  supportedPaymentNetworks: [PAYMENT_NETWORK_ACH]
}

export const DEV_EXTERNAL_BANK_ACCOUNT_FORM: ExternalBankAccountForm = {
  nickname: 'Test Bank Account',
  accountHolderType: EXTERNAL_BANK_ACCOUNT_HOLDER_TYPE_INDIVIDUAL,
  accountType: EXTERNAL_BANK_ACCOUNT_TYPE_CHECKING,
  accountNumber: '000123456789',
  routingNumber: '110000000',
  recipientName: 'Recipient Name',
  recipientEmail: 'recipient@example.com',
  recipientPhone: '1234567890',
  address: { line1: '123 Main St', line2: '', city: 'New York', state: 'NY', postal_code: '10001', country: 'US' },
  supportedPaymentNetworks: [PAYMENT_NETWORK_ACH]
}

export function initialExternalBankAccountForm(): ExternalBankAccountForm {
  return isProductionAppEnv(getAppEnv()) ? EMPTY_EXTERNAL_BANK_ACCOUNT_FORM : DEV_EXTERNAL_BANK_ACCOUNT_FORM
}

export type ExternalBankAccountFormSource = {
  accountDetails?: Partial<AccountDetails>
  billingDetails?: Partial<BillingDetails>
  supportedPaymentNetworks?: BankPaymentNetwork[]
}

export function externalBankAccountReadToForm(source: ExternalBankAccountFormSource): ExternalBankAccountForm {
  const a = source.accountDetails ?? {}
  const b = source.billingDetails ?? {}
  const addr = (b.address ?? {}) as Partial<ExternalBankAccountAddressForm>
  return {
    nickname: a.name ?? '',
    accountHolderType: a.account_holder_type ?? EXTERNAL_BANK_ACCOUNT_HOLDER_TYPE_INDIVIDUAL,
    accountType: a.account_type ?? EXTERNAL_BANK_ACCOUNT_TYPE_CHECKING,
    accountNumber: a.account_number ?? '',
    routingNumber: a.routing_number ?? '',
    recipientName: b.name ?? '',
    recipientEmail: b.email ?? '',
    recipientPhone: b.phone ?? '',
    address: {
      line1: addr.line1 ?? '',
      line2: addr.line2 ?? '',
      city: addr.city ?? '',
      state: addr.state ?? '',
      postal_code: addr.postal_code ?? '',
      country: addr.country ?? ''
    },
    supportedPaymentNetworks: source.supportedPaymentNetworks ?? [PAYMENT_NETWORK_ACH]
  }
}

export function seedExternalBankAccountFormAddress(
  form: ExternalBankAccountForm,
  seed: Partial<AddressJson> | null | undefined
): ExternalBankAccountForm {
  if (form.address.line1 || form.address.city) return form
  if (seed == null) return form
  return {
    ...form,
    address: {
      line1: seed.line1 ?? '',
      line2: seed.line2 ?? '',
      city: seed.city ?? '',
      state: seed.state ?? '',
      postal_code: seed.postalCode ?? '',
      country: seed.country ?? COUNTRY_CODE_US
    }
  }
}

function isFormFieldDirty(
  dirty: ExternalBankAccountFormDirty,
  key: Exclude<keyof ExternalBankAccountForm, 'address'>
): boolean {
  return dirty[key] === true
}

function isFormAddressDirty(dirty: ExternalBankAccountFormDirty, key: keyof ExternalBankAccountAddressForm): boolean {
  return dirty.address === true || (isObject(dirty.address) && dirty.address[key] === true)
}

function buildWireAccountDetails(form: ExternalBankAccountForm): AccountDetails {
  return {
    name: form.nickname.trim(),
    account_holder_type: form.accountHolderType,
    account_type: form.accountType,
    account_number: form.accountNumber.trim(),
    routing_number: form.routingNumber.trim()
  }
}

function buildWireBillingDetails(form: ExternalBankAccountForm): BillingDetails {
  const wireSupported = form.supportedPaymentNetworks.includes('us_domestic_wire')
  const billing: BillingDetails = {
    name: form.recipientName.trim(),
    email: form.recipientEmail.trim().toLowerCase(),
    phone: form.recipientPhone.trim()
  }
  if (wireSupported) {
    billing.address = {
      line1: form.address.line1.trim(),
      line2: form.address.line2.trim(),
      city: form.address.city.trim(),
      state: form.address.state.trim(),
      postal_code: form.address.postal_code.trim(),
      country: form.address.country.trim()
    }
  }
  return billing
}

export function externalBankAccountFormToCreateWire<
  CreateWire extends {
    accountDetails: AccountDetails
    billingDetails: BillingDetails
    supportedPaymentNetworks: BankPaymentNetwork[]
  }
>(form: ExternalBankAccountForm): CreateWire {
  return {
    accountDetails: buildWireAccountDetails(form),
    billingDetails: buildWireBillingDetails(form),
    supportedPaymentNetworks: form.supportedPaymentNetworks
  } as CreateWire
}

export function externalBankAccountFormToUpdateWire<
  UpdateWire extends {
    accountDetails?: AccountDetails
    billingDetails?: BillingDetails
    supportedPaymentNetworks?: BankPaymentNetwork[]
  }
>(form: ExternalBankAccountForm, dirty: ExternalBankAccountFormDirty): UpdateWire {
  const wire: UpdateWire = {} as UpdateWire

  const accountDirty =
    isFormFieldDirty(dirty, 'nickname') ||
    isFormFieldDirty(dirty, 'accountHolderType') ||
    isFormFieldDirty(dirty, 'accountType') ||
    isFormFieldDirty(dirty, 'accountNumber') ||
    isFormFieldDirty(dirty, 'routingNumber')
  if (accountDirty) (wire as Record<string, unknown>).accountDetails = buildWireAccountDetails(form)

  const addressKeys: (keyof ExternalBankAccountAddressForm)[] = [
    'line1',
    'line2',
    'city',
    'state',
    'postal_code',
    'country'
  ]
  const anyAddressDirty = addressKeys.some((k) => isFormAddressDirty(dirty, k))
  const billingDirty =
    isFormFieldDirty(dirty, 'recipientName') ||
    isFormFieldDirty(dirty, 'recipientEmail') ||
    isFormFieldDirty(dirty, 'recipientPhone') ||
    anyAddressDirty
  if (billingDirty) (wire as Record<string, unknown>).billingDetails = buildWireBillingDetails(form)

  if (isFormFieldDirty(dirty, 'supportedPaymentNetworks')) {
    ;(wire as Record<string, unknown>).supportedPaymentNetworks = form.supportedPaymentNetworks
  }

  return wire
}
